import { SpaceBetween, Tabs } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import ServiceCollection from 'src/services/ServiceCollection';
import AllocationCalculations from './AllocationCalculations';
import ManageAllocationSheets from './ManageAllocationSheets';
import CommonBalancePools from './common-balance-pools/CommonBalancePools';
import { MultidimensionalAllocationContext } from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import { multidimensionalAllocationReducer } from 'src/services/multidimensional-allocation/MultidimensionalAllocationReducer';
import { initialState } from 'src/services/multidimensional-allocation/MultidimensionalAllocationState';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { getFeatureFlags } from 'src/services/FeatureFlagsService';

interface MultidimensionalAllocationViewProps {
    services: ServiceCollection;
}

/**
 * Main view component for multidimensional allocation functionality
 * Provides tabbed interface for allocation calculations and sheet management
 */
export default function MultidimensionalAllocationView(props: MultidimensionalAllocationViewProps) {
    const { services } = props;
    
    const [activeTabId, setActiveTabId] = useState('allocation-calculations');

    // Feature flag gate for the Common Balance Pools tab (MDA 1.1 Phase 2).
    // Ships dark: enabled in local dev, defaults off in deployed stages.
    const [commonBalancePoolPhaseTwoEnabled, setCommonBalancePoolPhaseTwoEnabled] = useState(false);
    useEffect(() => {
        getFeatureFlags().then(flags => setCommonBalancePoolPhaseTwoEnabled(flags.commonBalancePoolPhaseTwoEnabled));
    }, []);

    // Set up the reducer for state management
    const [state, dispatch] = useReducerWithLogger(multidimensionalAllocationReducer, initialState);

    const tabs = [
        {
            label: 'Allocation calculations',
            id: 'allocation-calculations',
            content: <AllocationCalculations />,
        },
        {
            label: 'Manage allocation sheets',
            id: 'manage-allocation-sheets',
            content: <ManageAllocationSheets />,
        },
        ...(commonBalancePoolPhaseTwoEnabled
            ? [{
                label: 'Common Balance Pools',
                id: 'common-balance-pools',
                content: <CommonBalancePools />,
            }]
            : []),
    ];

    return (
        <MultidimensionalAllocationContext.Provider
            value={{
                services,
                multidimensionalAllocationState: state,
                multidimensionalAllocationDispatch: dispatch,
            }}
        >
            <SpaceBetween direction="vertical" size="l">
                <Tabs
                    activeTabId={activeTabId}
                    onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                    tabs={tabs}
                    data-class="multidimensionalAllocationTabs"
                />
            </SpaceBetween>
        </MultidimensionalAllocationContext.Provider>
    );
}
